import { useState, useRef } from 'react';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';

const PROJECT_TYPES = [
  'Landscape Design',
  'Hardscaping (Patio / Walls)',
  'Outdoor Living Space',
  'Irrigation System',
  'Lawn Installation',
  'Lighting System',
  'Full Property Transformation',
  'Other',
];

const TRUST_SIGNALS = [
  { icon: 'ri-shield-check-line', text: 'Fully Licensed & Insured' },
  { icon: 'ri-star-fill', text: '4.9★ Rated on Google' },
  { icon: 'ri-home-heart-line', text: 'Locally Owned & Operated' },
  { icon: 'ri-calendar-check-line', text: 'Fast Response — 24hr Callback' },
];

type FormState = 'idle' | 'submitting' | 'success' | 'error';

export default function FinalCTA() {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersectionObserver(ref, { threshold: 0.1 });
  const [formState, setFormState] = useState<FormState>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    setFormState('submitting');

    const form = e.currentTarget;
    const data = new URLSearchParams();
    new FormData(form).forEach((val, key) => data.append(key, val.toString()));

    try {
      const res = await fetch('https://readdy.ai/api/form/d70jlt9qa83q0hmd1ap0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setFormState('success');
        form.reset();
        setCharCount(0);
      } else {
        setFormState('error');
      }
    } catch {
      setFormState('error');
    }
  };

  return (
    <section
      id="consultation"
      className="relative py-28 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://brainardsgreenscapes.com/assets/images/after4.jpeg"
          alt=""
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-charcoal-900/90 via-charcoal-900/80 to-forest-900/85" />
      </div>

      <div
        ref={ref}
        className={`relative z-10 max-w-7xl mx-auto px-8 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="grid md:grid-cols-2 gap-16 items-start">
          {/* Left: Copy */}
          <div className="pt-4">
            <p className="font-sans text-stone-400 text-xs tracking-[0.25em] uppercase mb-5">Get Started Today</p>
            <h2 className="font-serif text-white font-semibold leading-tight mb-6" style={{ fontSize: 'clamp(32px, 4vw, 56px)' }}>
              Transform Your Outdoor Space Into Something<br />
              <em className="italic text-stone-300">Exceptional</em>
            </h2>
            <p className="font-sans text-stone-300 text-base leading-relaxed mb-10">
              Schedule your free consultation today and start building a landscape designed to impress and built to last for decades.
            </p>

            {/* Trust signals */}
            <div className="grid grid-cols-2 gap-4 mb-10">
              {TRUST_SIGNALS.map((sig) => (
                <div key={sig.text} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-forest-500/30 rounded-lg flex-shrink-0">
                    <i className={`${sig.icon} text-stone-300 text-base`} />
                  </div>
                  <span className="font-sans text-stone-300 text-xs">{sig.text}</span>
                </div>
              ))}
            </div>

            {/* Benefits list */}
            <div className="space-y-3 mb-10">
              {[
                'Complimentary on-site consultation',
                'Custom design presentation included',
                'Transparent, detailed project proposal',
                'No pressure — zero obligation',
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-double-line text-forest-400 text-base" />
                  </div>
                  <span className="font-sans text-stone-300 text-sm">{item}</span>
                </div>
              ))}
            </div>

            <a
              href="tel:5756358883"
              className="inline-flex items-center gap-3 text-white font-sans text-base font-medium cursor-pointer"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-forest-500 rounded-full">
                <i className="ri-phone-fill text-white text-lg" />
              </div>
              <div>
                <div className="text-xs text-stone-400 font-normal uppercase tracking-wider">Call us directly</div>
                <div className="text-white font-semibold text-base">(575) 635-8883</div>
              </div>
            </a>
          </div>

          {/* Right: Form */}
          <div className="bg-white rounded-lg overflow-hidden">
            {/* Form trust header */}
            <div className="bg-forest-600 px-7 py-5">
              <p className="font-sans text-stone-200 text-xs tracking-[0.15em] uppercase mb-1">Trusted by Local Homeowners Since 2005</p>
              <h3 className="font-serif text-white text-xl font-semibold">Request Your Free Consultation</h3>
            </div>

            <div className="p-7">
              {formState === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center bg-forest-50 rounded-full mx-auto mb-5">
                    <i className="ri-check-double-line text-forest-500 text-3xl" />
                  </div>
                  <h3 className="font-serif text-charcoal-900 text-2xl font-semibold mb-3">Request Received!</h3>
                  <p className="font-sans text-charcoal-700 text-sm leading-relaxed">
                    Thank you! Our team will reach out within 24 hours to schedule your complimentary consultation.
                  </p>
                </div>
              ) : (
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                  id="consultation-form"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block font-sans text-charcoal-800 text-xs font-semibold mb-1.5 uppercase tracking-wide">Full Name *</label>
                      <input
                        name="name"
                        required
                        type="text"
                        placeholder="John Smith"
                        className="w-full border border-stone-200 rounded-md px-3.5 py-3 font-sans text-sm text-charcoal-900 placeholder:text-stone-400 focus:outline-none focus:border-forest-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block font-sans text-charcoal-800 text-xs font-semibold mb-1.5 uppercase tracking-wide">Phone *</label>
                      <input
                        name="phone"
                        required
                        type="tel"
                        placeholder="(575) 000-0000"
                        className="w-full border border-stone-200 rounded-md px-3.5 py-3 font-sans text-sm text-charcoal-900 placeholder:text-stone-400 focus:outline-none focus:border-forest-400 transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block font-sans text-charcoal-800 text-xs font-semibold mb-1.5 uppercase tracking-wide">Email Address *</label>
                    <input
                      name="email"
                      required
                      type="email"
                      placeholder="you@email.com"
                      className="w-full border border-stone-200 rounded-md px-3.5 py-3 font-sans text-sm text-charcoal-900 placeholder:text-stone-400 focus:outline-none focus:border-forest-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-charcoal-800 text-xs font-semibold mb-1.5 uppercase tracking-wide">Project Type</label>
                    <select
                      name="project_type"
                      className="w-full border border-stone-200 rounded-md px-3.5 py-3 font-sans text-sm text-charcoal-900 focus:outline-none focus:border-forest-400 transition-colors bg-white cursor-pointer"
                    >
                      <option value="">Select project type...</option>
                      {PROJECT_TYPES.map((pt) => (
                        <option key={pt} value={pt}>{pt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block font-sans text-charcoal-800 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                      Tell Us About Your Project
                      <span className={`ml-2 font-normal normal-case tracking-normal ${charCount > 500 ? 'text-red-500' : 'text-stone-400'}`}>
                        {charCount}/500
                      </span>
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      placeholder="Describe your vision, property size, timeline..."
                      maxLength={500}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full border border-stone-200 rounded-md px-3.5 py-3 font-sans text-sm text-charcoal-900 placeholder:text-stone-400 focus:outline-none focus:border-forest-400 transition-colors resize-none"
                    />
                    {charCount > 500 && (
                      <p className="font-sans text-red-500 text-xs mt-1">Message cannot exceed 500 characters.</p>
                    )}
                  </div>

                  {formState === 'error' && (
                    <p className="font-sans text-red-500 text-xs">Something went wrong. Please try again or call us directly.</p>
                  )}

                  <button
                    type="submit"
                    disabled={formState === 'submitting' || charCount > 500}
                    className="w-full bg-forest-500 hover:bg-forest-600 disabled:opacity-60 text-white font-sans font-bold text-sm py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap tracking-wide"
                  >
                    {formState === 'submitting' ? 'Sending...' : 'Request My Free Consultation →'}
                  </button>
                  <p className="font-sans text-stone-400 text-[11px] text-center">
                    No spam. No pressure. We respect your privacy.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
